import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { motion, Reorder, useDragControls } from 'framer-motion';
import {
  Badge,
  Button,
  EmptyState,
  SectionHeading,
  Skeleton,
  Surface,
  feel,
  ghostButtonStyle,
  primaryButtonStyle,
  srOnly as queueSrOnly,
} from '@kilat/ui';

import { useAuth } from '../lib/use-auth.js';
import { useMyRole } from '../lib/use-my-role.js';
import { WasherQueue } from '../components/floor/washer-queue.js';
import { useI18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { logger } from '../lib/logger.js';
import { computeToPosition } from '../lib/queue-reorder.js';
import { SUB_STAGE_ORDER, nextSubStage } from '../lib/queue-substage.js';
import { bayDisplayName, resolveBayName } from '../lib/bay-name.js';
import { baysRepoFor, outletDocRepoFor, ticketsRepoFor } from '../lib/repositories/outlet-repos.js';
import { callAdvanceTicket, callReorderQueue, callSetSubStage } from '../lib/callables/queue.js';
import {
  CatalogSettingsSchema,
  money,
  type Bay,
  type Ticket,
  type TicketStatus,
  type Timestamp,
  type TicketSubStage,
} from '@kilat/shared';

/**
 * /queue — operator Kanban.
 *
 * Sprint 4:
 *   - Picks the first outlet for the active tenant (multi-outlet switcher Sprint 6).
 *   - Subscribes to the active queue collection (`listSnapshot`).
 *   - Three columns: In Queue / In Washing Process / Ready for Collection.
 *   - Per-card actions wired to `advanceTicket`:
 *       In Queue           → "Start" (→ inWashingProcess) | "Cancel"
 *       In Washing Process → "Ready" (→ readyForCollection) | "Cancel"
 *       Ready              → "Collected" (→ collected — archives)
 *
 * Sprint 5+: drag-to-reorder, hold/resume, bay assignment, sub-stages.
 */

const VISIBLE_COLUMNS: readonly { status: TicketStatus; key: keyof typeof I18N_STATUS_KEY }[] = [
  { status: 'inQueue', key: 'inQueue' },
  { status: 'inWashingProcess', key: 'inWashingProcess' },
  { status: 'readyForCollection', key: 'readyForCollection' },
];

/**
 * Each column's tone in the shared vocabulary (`@kilat/ui`). Before this the
 * three columns were identical white cards and status was carried ONLY by
 * horizontal position, so a washer glancing at a tablet across the bay had to
 * read headings to find the ready cars. The rail, the heading dot and the count
 * badge now all take the same tone, so they cannot drift apart.
 */
const COLUMN_TONE: Record<string, 'neutral' | 'info' | 'success'> = {
  inQueue: 'neutral',
  inWashingProcess: 'info',
  readyForCollection: 'success',
};

const I18N_STATUS_KEY = {
  scheduled: 'queueStatus.scheduled',
  inQueue: 'queueStatus.inQueue',
  inWashingProcess: 'queueStatus.inWashingProcess',
  readyForCollection: 'queueStatus.readyForCollection',
  collected: 'queueStatus.collected',
  cancelled: 'queueStatus.cancelled',
  hold: 'queueStatus.hold',
} as const;

/** Epoch millis of a ticket's scheduled appointment time, or 0 when absent. */
function scheduledForMsOf(ticket: Ticket): number {
  return ticket.scheduledFor?.toMillis?.() ?? 0;
}

/**
 * Read shape of the active outlet doc we care about here — only
 * `catalogSettings` (parsed via `CatalogSettingsSchema` for `allowSubStages`).
 * The repo schema types it as `unknown`; we narrow with a safe parse.
 */
interface OutletRead {
  readonly catalogSettings?: unknown;
}

export function QueuePage() {
  const { claims, user } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const {
    outletId,
    loading: outletLoading,
    error: outletError,
    retry: retryOutlets,
  } = useActiveOutlet();
  // Bump to force the snapshot listener to re-subscribe (retry after an error).
  // Threaded through the tracked-snapshot KEY (not the memo deps) so the hook
  // re-subscribes on change without an unused-dep on the subscribe factory.
  const [reloadTick, setReloadTick] = useState(0);

  const subscribe = useMemo(() => {
    if (!tenantId || !outletId) return null;
    const repo = ticketsRepoFor(tenantId, outletId);
    return (cb: (vs: Ticket[]) => void) => repo.listSnapshot(cb);
  }, [tenantId, outletId]);

  const {
    data: tickets,
    loading,
    error,
  } = useTrackedSnapshot<Ticket[]>(['queue', tenantId, outletId, reloadTick], subscribe);

  // Live-subscribe to the active outlet doc so the per-outlet `allowSubStages`
  // opt-in (and the owner/manager toggle) reflect changes without a reload.
  const outletSubscribe = useMemo(() => {
    if (!tenantId || !outletId) return null;
    const repo = outletDocRepoFor(tenantId);
    return (cb: (v: OutletRead | null) => void) => repo.snapshot(outletId, cb);
  }, [tenantId, outletId]);
  const { data: outletDoc } = useTrackedSnapshot<OutletRead | null>(
    ['queue-outlet', tenantId, outletId],
    outletSubscribe,
  );
  const allowSubStages = useMemo(() => {
    const parsed = CatalogSettingsSchema.safeParse(outletDoc?.catalogSettings ?? {});
    return parsed.success ? parsed.data.allowSubStages : false;
  }, [outletDoc]);

  // Live-subscribe to this outlet's wash bays so the picker, the board chip,
  // and the config modal all reflect changes without a reload.
  const baysSubscribe = useMemo(() => {
    if (!tenantId || !outletId) return null;
    const repo = baysRepoFor(tenantId, outletId);
    return (cb: (vs: Bay[]) => void) => repo.listSnapshot(cb);
  }, [tenantId, outletId]);
  const { data: baysData } = useTrackedSnapshot<Bay[]>(
    ['queue-bays', tenantId, outletId],
    baysSubscribe,
  );
  const bays = useMemo(() => baysData ?? [], [baysData]);
  const activeBays = useMemo(() => bays.filter((b) => b.active), [bays]);

  // Only owner/manager may flip the per-outlet sub-stage opt-in or manage bays.
  const myRole = useMyRole();
  const canManageSubStages = myRole === 'owner' || myRole === 'manager';
  const canManageBays = myRole === 'owner' || myRole === 'manager';
  // Admit/Cancel of appointments — owner/manager/cashier (mirrors the
  // scheduled→inQueue / scheduled→cancelled roles in the state machine; the
  // server is the real gate, this just hides the buttons for washers).
  const canManageAppointments = myRole === 'owner' || myRole === 'manager' || myRole === 'cashier';

  // Client-side clock used to auto-surface due appointments — no scheduled
  // Cloud Function (honours the ~$0-idle cost rule). Re-derives every 30s so a
  // booking whose time arrives while the board is open moves itself into the
  // live queue view without a reload.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Non-blocking "couldn't reorder" banner. A reorder that the server rejects
  // must never leave the board showing a stale order (the list component reverts
  // to server truth), and the operator must be told so they can retry — otherwise
  // a silent `logger.error` is a dead-end. Auto-dismisses; also dismissable.
  const [reorderError, setReorderError] = useState(false);
  useEffect(() => {
    if (!reorderError) return;
    const id = setTimeout(() => setReorderError(false), 5000);
    return () => clearTimeout(id);
  }, [reorderError]);

  // Same non-dead-end contract for a rejected sub-stage change: the card reverts
  // to server truth (SubStageControl snaps back) and this banner tells the
  // operator so they can retry. Auto-dismisses; also dismissable.
  const [subStageError, setSubStageError] = useState(false);
  useEffect(() => {
    if (!subStageError) return;
    const id = setTimeout(() => setSubStageError(false), 5000);
    return () => clearTimeout(id);
  }, [subStageError]);

  // Same non-dead-end contract for a rejected Start-with-a-bay: the car never
  // moved (the board reflects server truth), and this banner tells the operator
  // so they can retry. Auto-dismisses; also dismissable.
  const [bayError, setBayError] = useState(false);
  useEffect(() => {
    if (!bayError) return;
    const id = setTimeout(() => setBayError(false), 5000);
    return () => clearTimeout(id);
  }, [bayError]);

  // Same non-dead-end contract for a rejected Admit/Cancel of an appointment.
  const [apptError, setApptError] = useState(false);
  useEffect(() => {
    if (!apptError) return;
    const id = setTimeout(() => setApptError(false), 5000);
    return () => clearTimeout(id);
  }, [apptError]);

  // The Start-Next bay chooser: the ticket awaiting a bay pick, or null when
  // closed. Only opened when the outlet has ≥1 active bay.
  const [bayPickerTicket, setBayPickerTicket] = useState<Ticket | null>(null);
  // Owner/manager bay-config modal.
  const [baysModalOpen, setBaysModalOpen] = useState(false);

  if (!tenantId) {
    return (
      <Splash
        label={t.t('dashboard.states.noTenant')}
        action={
          <button type="button" onClick={() => window.location.reload()} style={primaryButtonStyle}>
            {t.t('dashboard.states.reload')}
          </button>
        }
      />
    );
  }
  if (outletError) {
    return (
      <Splash
        label={t.t('dashboard.states.queueError')}
        action={
          <button type="button" onClick={retryOutlets} style={primaryButtonStyle}>
            {t.t('common.tryAgain')}
          </button>
        }
      />
    );
  }
  if (outletLoading) return <QueueBoardSkeleton label={t.t('common.loading')} />;
  if (!outletId) {
    return (
      <Splash
        label={t.t('dashboard.states.noOutlets')}
        action={
          <Link to="/welcome" style={ctaLinkStyle}>
            {t.t('dashboard.states.finishSetup')}
          </Link>
        }
      />
    );
  }
  if (error) {
    return (
      <Splash
        label={t.t('dashboard.states.queueError')}
        action={
          <button
            type="button"
            onClick={() => setReloadTick((n) => n + 1)}
            style={primaryButtonStyle}
          >
            {t.t('common.tryAgain')}
          </button>
        }
      />
    );
  }
  if (loading) return <QueueBoardSkeleton label={t.t('common.loading')} />;

  // The washer's board is a different screen, not a trimmed one: symbols and
  // plates instead of columns and words, no money, one button per car. See
  // components/floor/washer-queue.tsx for who it is built for.
  if (myRole === 'washer' && user) {
    return (
      <main
        className="min-h-dvh"
        style={{
          background: 'var(--tint-subtle)',
          color: 'var(--color-on-surface)',
          padding: '0 var(--space-3) var(--space-8)',
        }}
      >
        <WasherQueue
          tickets={tickets ?? []}
          bays={bays}
          allowSubStages={allowSubStages}
          tenantId={tenantId}
          outletId={outletId}
          uid={user.uid}
          role={myRole}
        />
      </main>
    );
  }

  const list = tickets ?? [];
  const inQueue = list
    .filter((t) => t.status === 'inQueue')
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const inWash = list.filter((t) => t.status === 'inWashingProcess');
  const ready = list.filter((t) => t.status === 'readyForCollection');
  const hold = list
    .filter((t) => t.status === 'hold')
    .sort((a, b) => {
      const aMs = a.updatedAt?.toMillis?.() ?? 0;
      const bMs = b.updatedAt?.toMillis?.() ?? 0;
      return aMs - bMs;
    });
  // Appointments: `scheduled` tickets are OUTSIDE the live queue (no position,
  // excluded from ordering/wait/active-count). Split by whether their time has
  // arrived: DUE ones surface at the top of the In-Queue column to be admitted;
  // UPCOMING ones sit in the "Upcoming appointments" section. Both sorted by time.
  const scheduled = list
    .filter((tk) => tk.status === 'scheduled')
    .sort((a, b) => scheduledForMsOf(a) - scheduledForMsOf(b));
  const scheduledDue = scheduled.filter((tk) => scheduledForMsOf(tk) <= nowMs);
  const scheduledUpcoming = scheduled.filter((tk) => scheduledForMsOf(tk) > nowMs);
  // Active count excludes not-yet-served appointments (they aren't "active").
  const activeCount = list.filter((tk) => tk.status !== 'scheduled').length;
  const byColumn: Record<TicketStatus, Ticket[]> = {
    scheduled: [],
    inQueue,
    inWashingProcess: inWash,
    readyForCollection: ready,
    collected: [],
    cancelled: [],
    hold,
  };

  async function advance(ticket: Ticket, to: TicketStatus) {
    if (!tenantId || !outletId) return;
    try {
      await callAdvanceTicket({ tenantId, outletId, ticketId: ticket.id, to });
    } catch (err) {
      logger.error('advanceTicket failed', { ticketId: ticket.id, to, error: String(err) });
    }
  }

  // Admit a scheduled appointment into the live queue (scheduled → inQueue; the
  // server assigns a tail position). Cancel removes a scheduled/no-show booking
  // (scheduled → cancelled). Both surface a banner on failure — no dead-ends.
  async function admitAppointment(ticket: Ticket): Promise<void> {
    if (!tenantId || !outletId) return;
    try {
      await callAdvanceTicket({ tenantId, outletId, ticketId: ticket.id, to: 'inQueue' });
      feel('success');
      setApptError(false);
    } catch (err) {
      logger.error('admitAppointment failed', { ticketId: ticket.id, error: String(err) });
      setApptError(true);
    }
  }
  async function cancelAppointment(ticket: Ticket): Promise<void> {
    if (!tenantId || !outletId) return;
    try {
      await callAdvanceTicket({ tenantId, outletId, ticketId: ticket.id, to: 'cancelled' });
      feel('tap');
      setApptError(false);
    } catch (err) {
      logger.error('cancelAppointment failed', { ticketId: ticket.id, error: String(err) });
      setApptError(true);
    }
  }

  // Commit a reorder through the existing transactional callable. Returns
  // whether it succeeded so the drag list can revert its optimistic order on
  // failure. Shared by the drag gesture and the arrow-button fallback.
  async function commitReorder(ticketId: string, toPosition: number): Promise<boolean> {
    if (!tenantId || !outletId) return false;
    try {
      await callReorderQueue({ tenantId, outletId, ticketId, toPosition });
      feel('success');
      setReorderError(false);
      return true;
    } catch (err) {
      logger.error('reorderQueue failed', { ticketId, toPosition, error: String(err) });
      setReorderError(true);
      return false;
    }
  }

  // Commit a sub-stage change through the dedicated callable. Returns whether
  // it succeeded so the card's control can revert its optimistic state on
  // failure (no dead-end — the board never shows a stage the server refused).
  async function commitSubStage(ticket: Ticket, next: TicketSubStage | null): Promise<boolean> {
    if (!tenantId || !outletId) return false;
    try {
      await callSetSubStage({ tenantId, outletId, ticketId: ticket.id, subStage: next ?? null });
      feel('success');
      setSubStageError(false);
      return true;
    } catch (err) {
      logger.error('setSubStage failed', {
        ticketId: ticket.id,
        next: next ?? null,
        error: String(err),
      });
      setSubStageError(true);
      return false;
    }
  }

  // Start a wash, optionally assigning a bay. Surfaces a visible error banner
  // on failure (the car stays in queue — the board reflects server truth, so
  // "revert" is automatic; no dead-end). Shared by the picker and the no-bays
  // fast path.
  async function startWash(ticket: Ticket, bayId?: string): Promise<void> {
    if (!tenantId || !outletId) return;
    try {
      await callAdvanceTicket({
        tenantId,
        outletId,
        ticketId: ticket.id,
        to: 'inWashingProcess',
        ...(bayId ? { bayId } : {}),
      });
      feel('success');
      setBayError(false);
    } catch (err) {
      logger.error('startWash failed', {
        ticketId: ticket.id,
        bayId: bayId ?? null,
        error: String(err),
      });
      setBayError(true);
    }
  }

  // Requesting a start opens the bay chooser when the outlet uses bays; with no
  // active bays it behaves exactly as before (direct start, no bayId) so outlets
  // that don't use bays are unaffected.
  function requestStart(ticket: Ticket): void {
    if (activeBays.length > 0) {
      setBayPickerTicket(ticket);
    } else {
      void startWash(ticket);
    }
  }

  function startNext() {
    const next = inQueue[0];
    if (!next) return;
    requestStart(next);
  }

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        {/* flexShrink:0 — without it the nav's growth squeezes this column until
            the h1 wraps INSIDE the word: the page title rendered as "Que / ue". */}
        <div style={{ flexShrink: 0 }}>
          <h1
            style={{
              fontSize: 'var(--text-h1)',
              fontWeight: 650,
              margin: 0,
              letterSpacing: '-0.02em',
              textWrap: 'nowrap',
            }}
          >
            {t.t('dashboard.queue.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 0,
            }}
          >
            {t.t('dashboard.queue.activeCount', { count: activeCount })}
          </p>
        </div>
      </header>

      {reorderError ? (
        <div role="alert" style={reorderErrorBannerStyle}>
          <span style={{ flex: 1, minWidth: 0 }}>{t.t('dashboard.queue.reorderFailed')}</span>
          <button
            type="button"
            onClick={() => setReorderError(false)}
            aria-label={t.t('dashboard.queue.dismissError')}
            style={dismissButtonStyle}
          >
            {DISMISS_GLYPH}
          </button>
        </div>
      ) : null}

      {subStageError ? (
        <div role="alert" style={reorderErrorBannerStyle}>
          <span style={{ flex: 1, minWidth: 0 }}>{t.t('dashboard.queue.subStage.failed')}</span>
          <button
            type="button"
            onClick={() => setSubStageError(false)}
            aria-label={t.t('dashboard.queue.dismissError')}
            style={dismissButtonStyle}
          >
            {DISMISS_GLYPH}
          </button>
        </div>
      ) : null}

      {bayError ? (
        <div role="alert" style={reorderErrorBannerStyle}>
          <span style={{ flex: 1, minWidth: 0 }}>{t.t('dashboard.queue.bay.startFailed')}</span>
          <button
            type="button"
            onClick={() => setBayError(false)}
            aria-label={t.t('dashboard.queue.dismissError')}
            style={dismissButtonStyle}
          >
            {DISMISS_GLYPH}
          </button>
        </div>
      ) : null}

      {apptError ? (
        <div role="alert" style={reorderErrorBannerStyle}>
          <span style={{ flex: 1, minWidth: 0 }}>
            {t.t('dashboard.queue.appointments.actionFailed')}
          </span>
          <button
            type="button"
            onClick={() => setApptError(false)}
            aria-label={t.t('dashboard.queue.dismissError')}
            style={dismissButtonStyle}
          >
            {DISMISS_GLYPH}
          </button>
        </div>
      ) : null}

      {/* Upcoming appointments — `scheduled` tickets whose time hasn't arrived.
          Sorted by scheduledFor. Due ones move to the In-Queue column below. */}
      {scheduledUpcoming.length > 0 ? (
        <UpcomingAppointmentsSection
          tickets={scheduledUpcoming}
          locale={t.locale}
          canManage={canManageAppointments}
          onCancel={(tk) => void cancelAppointment(tk)}
        />
      ) : null}

      <ShiftSummary waiting={inQueue} washing={inWash} ready={ready} />

      <div
        style={{
          display: 'grid',
          // auto-fit, NOT a hard 3: three fixed columns forced 418px of content
          // into a 375px phone and the whole page scrolled sideways. The board
          // is explicitly a tablet/phone surface for washers, so it stacks.
          gridTemplateColumns: BOARD_COLUMNS,
          gap: 'var(--space-4)',
          alignItems: 'start',
        }}
      >
        {VISIBLE_COLUMNS.map((col, colIndex) => {
          const columnTickets = byColumn[col.status];
          const isQueueCol = col.status === 'inQueue';
          return (
            <Surface
              key={col.status}
              className="k-rise"
              level={2}
              accent={COLUMN_TONE[col.status]}
              style={
                { paddingTop: 'var(--space-3)', minHeight: 180, '--k-i': colIndex } as CSSProperties
              }
            >
              <SectionHeading
                dot={COLUMN_TONE[col.status]}
                trailing={<Badge tone={COLUMN_TONE[col.status]}>{columnTickets.length}</Badge>}
              >
                {t.t(I18N_STATUS_KEY[col.key])}
              </SectionHeading>

              {/* Column-header action: Start Next on the In-Queue column */}
              {isQueueCol && columnTickets.length > 0 ? (
                <button
                  type="button"
                  className="k-press"
                  onClick={() => {
                    feel('tap');
                    void startNext();
                  }}
                  style={{
                    width: '100%',
                    marginBottom: 'var(--space-3)',
                    padding: '10px 14px',
                    background: 'var(--color-primary)',
                    color: 'var(--color-primary-fg)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontWeight: 500,
                    fontSize: 'var(--text-meta)',
                    cursor: 'pointer',
                    boxShadow: 'var(--shadow-1)',
                  }}
                >
                  {t.t('dashboard.queue.startNext', { number: columnTickets[0]?.number ?? '—' })}
                </button>
              ) : null}

              {/* Due appointments surface at the TOP of the live queue —
                  flagged "Appointment", admitted (→ inQueue) or cancelled. They
                  are NOT part of the reorderable/positioned list below. */}
              {isQueueCol && scheduledDue.length > 0 ? (
                <DueAppointmentsStrip
                  tickets={scheduledDue}
                  locale={t.locale}
                  canManage={canManageAppointments}
                  onAdmit={(tk) => void admitAppointment(tk)}
                  onCancel={(tk) => void cancelAppointment(tk)}
                />
              ) : null}

              {isQueueCol ? (
                <ReorderableQueueList
                  tickets={columnTickets}
                  onAdvance={(ticket, to) => {
                    if (to === 'inWashingProcess') requestStart(ticket);
                    else void advance(ticket, to);
                  }}
                  onCommitReorder={commitReorder}
                />
              ) : (
                <motion.div layout style={{ display: 'grid', gap: 'var(--space-2)' }}>
                  {columnTickets.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      onAdvance={(to) => void advance(ticket, to)}
                      bayChip={
                        col.status === 'inWashingProcess' && ticket.bayId ? (
                          <BayChip name={resolveBayName(ticket.bayId, bays, t.locale)} />
                        ) : null
                      }
                      subStageControl={
                        col.status === 'inWashingProcess' && allowSubStages ? (
                          <SubStageControl
                            current={ticket.subStage ?? null}
                            onSet={(next) => commitSubStage(ticket, next)}
                          />
                        ) : null
                      }
                    />
                  ))}
                </motion.div>
              )}
              {columnTickets.length === 0 && !(isQueueCol && scheduledDue.length > 0) ? (
                <EmptyState title={t.t('dashboard.queue.columnEmpty')} />
              ) : null}

              {/* Hold parking-lot — only on the In Queue column */}
              {isQueueCol && hold.length > 0 ? (
                <HoldSection
                  tickets={hold}
                  onResume={(tk) => void advance(tk, 'inQueue')}
                  onCancel={(tk) => void advance(tk, 'cancelled')}
                />
              ) : null}
            </Surface>
          );
        })}
      </div>

      {bayPickerTicket ? (
        <BayPicker
          ticket={bayPickerTicket}
          bays={activeBays}
          onPick={(bayId) => {
            const ticket = bayPickerTicket;
            setBayPickerTicket(null);
            void startWash(ticket, bayId ?? undefined);
          }}
          onCancel={() => setBayPickerTicket(null)}
        />
      ) : null}

      {/* Board setup — deliberately BELOW the board. Both are configured once
          and then ignored, yet they used to sit ABOVE the cars: the first thing
          on the busiest screen in the product was a pair of settings rows, and
          the actual work started half a screen down. */}
      {canManageSubStages ? (
        <SubStagesSettingToggle
          tenantId={tenantId}
          outletId={outletId}
          enabled={allowSubStages}
          onError={() => setSubStageError(true)}
        />
      ) : null}

      {canManageBays ? (
        <BaysSettingPanel
          totalCount={bays.length}
          activeCount={activeBays.length}
          onManage={() => setBaysModalOpen(true)}
        />
      ) : null}

      {baysModalOpen && tenantId && outletId ? (
        <BaysManagerModal
          tenantId={tenantId}
          outletId={outletId}
          bays={bays}
          onClose={() => setBaysModalOpen(false)}
        />
      ) : null}
    </main>
  );
}

/**
 * The drag-to-reorder In-Queue column. Wraps the sorted `tickets` in a Framer
 * `Reorder.Group` over a LOCAL ordered copy so dragging feels instant, then
 * commits the settled order through the existing `reorderQueue` callable.
 *
 * Reconciliation: the local order is re-seeded from `tickets` whenever the live
 * snapshot's id+position signature changes, so server truth always wins after a
 * commit (or an externally-driven reorder). On a rejected commit the local order
 * is reverted to `tickets` immediately — the board never shows an order the
 * server refused. Arrow buttons remain the keyboard-accessible fallback.
 */
function ReorderableQueueList({
  tickets,
  onAdvance,
  onCommitReorder,
}: {
  readonly tickets: readonly Ticket[];
  readonly onAdvance: (ticket: Ticket, to: TicketStatus) => void;
  readonly onCommitReorder: (ticketId: string, toPosition: number) => Promise<boolean>;
}) {
  const [localOrder, setLocalOrder] = useState<Ticket[]>(() => [...tickets]);
  const localOrderRef = useRef<Ticket[]>(localOrder);
  localOrderRef.current = localOrder;
  const dragFromRef = useRef<number | null>(null);

  // Re-seed from server truth whenever the live snapshot's value-identity
  // (id + position of every row) changes. Keying on the derived signature — not
  // the array ref — avoids re-seeding on snapshots that didn't touch the order.
  const signature = tickets.map((tk) => `${tk.id}:${tk.position ?? 0}`).join('|');
  useEffect(() => {
    setLocalOrder([...tickets]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  function handleDragStart(ticketId: string): void {
    dragFromRef.current = localOrderRef.current.findIndex((tk) => tk.id === ticketId);
    feel('tap');
  }

  async function handleDragEnd(ticketId: string): Promise<void> {
    const from = dragFromRef.current;
    dragFromRef.current = null;
    const order = localOrderRef.current;
    const toIndex = order.findIndex((tk) => tk.id === ticketId);
    if (from === null || toIndex < 0) return;
    const toPosition = computeToPosition(from, toIndex, order.length);
    if (toPosition === null) return; // settled where it started — no round-trip
    const ok = await onCommitReorder(ticketId, toPosition);
    if (!ok) setLocalOrder([...tickets]); // server rejected — snap back to its truth
  }

  // Arrow fallback: non-optimistic (the live snapshot re-seeds the order on
  // success), so keyboard users get the same guarantees without the gesture.
  async function handleArrow(ticketId: string, fromIndex: number, delta: number): Promise<void> {
    const toPosition = computeToPosition(
      fromIndex,
      fromIndex + delta,
      localOrderRef.current.length,
    );
    if (toPosition === null) return;
    await onCommitReorder(ticketId, toPosition);
  }

  return (
    <Reorder.Group
      as="div"
      axis="y"
      values={localOrder}
      onReorder={setLocalOrder}
      style={{ display: 'grid', gap: 'var(--space-2)' }}
    >
      {localOrder.map((ticket, idx) => (
        <ReorderableTicketCard
          key={ticket.id}
          ticket={ticket}
          canMoveUp={idx > 0}
          canMoveDown={idx < localOrder.length - 1}
          onAdvance={(to) => onAdvance(ticket, to)}
          onMoveUp={() => void handleArrow(ticket.id, idx, -1)}
          onMoveDown={() => void handleArrow(ticket.id, idx, 1)}
          onDragStart={() => handleDragStart(ticket.id)}
          onDragEnd={() => void handleDragEnd(ticket.id)}
        />
      ))}
    </Reorder.Group>
  );
}

/** One In-Queue card as a Framer `Reorder.Item`. `dragListener={false}` means a
 *  drag can ONLY be initiated by the grip handle (via `dragControls.start`),
 *  leaving the advance/hold/cancel/arrow buttons fully clickable. */
function ReorderableTicketCard({
  ticket,
  canMoveUp,
  canMoveDown,
  onAdvance,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragEnd,
}: {
  readonly ticket: Ticket;
  readonly canMoveUp: boolean;
  readonly canMoveDown: boolean;
  readonly onAdvance: (to: TicketStatus) => void;
  readonly onMoveUp: () => void;
  readonly onMoveDown: () => void;
  readonly onDragStart: () => void;
  readonly onDragEnd: () => void;
}) {
  const t = useI18n();
  const controls = useDragControls();
  return (
    <Reorder.Item
      as="div"
      value={ticket}
      dragListener={false}
      dragControls={controls}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <TicketCard
        ticket={ticket}
        showReorder
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        onAdvance={onAdvance}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onDragHandlePointerDown={(e) => controls.start(e)}
        dragHandleLabel={t.t('dashboard.queue.dragHandle')}
      />
    </Reorder.Item>
  );
}

/**
 * How long this car has been on the board.
 *
 * A queue board without elapsed time is missing the one number the job turns
 * on: an operator's next decision is almost always "who has been waiting
 * longest", and until now the screen could not answer it. Turns amber past 20
 * minutes and red past 40, so the answer is visible without reading digits.
 */
function ElapsedChip({ since }: { readonly since: Timestamp | undefined }) {
  const t = useI18n();
  // `toMillis` is optional on the shared Timestamp interface — seed and test
  // fixtures carry a bare { seconds, nanoseconds } with no prototype methods.
  const atMs = since ? (since.toMillis ? since.toMillis() : since.seconds * 1000) : null;
  if (atMs === null) return null;
  const ms = Date.now() - atMs;
  if (ms < 0) return null;
  const mins = Math.floor(ms / 60_000);
  const label =
    mins >= 60
      ? t.t('dashboard.queue.elapsedHr', { hrs: String(Math.floor(mins / 60)) })
      : t.t('dashboard.queue.elapsedMin', { mins: String(mins) });
  const tone =
    mins >= 40
      ? { fg: 'var(--color-danger)', bg: 'var(--color-danger-bg)' }
      : mins >= 20
        ? { fg: 'var(--color-warn)', bg: 'var(--color-warn-bg)' }
        : { fg: 'var(--color-on-surface-muted)', bg: 'var(--color-border)' };
  return (
    <span
      style={{
        fontSize: '0.6875rem',
        fontWeight: 600,
        color: tone.fg,
        background: tone.bg,
        borderRadius: 'var(--radius-pill)',
        padding: '0.1em 0.5em',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

/**
 * The shift at a glance.
 *
 * The board showed an operator their WORK but never their SHIFT — how many are
 * waiting, how long the worst wait has run, and (the number nobody had on
 * screen anywhere) how much money is sitting in finished cars nobody has paid
 * for yet. That last one is the whole reason a ready-for-collection column
 * exists, and it was only ever visible by adding up cards by eye.
 *
 * Derived entirely from tickets already loaded for the board — no extra
 * listener, no extra document read. On a project whose ceiling is the Firestore
 * free tier, a summary that costs nothing is the only kind worth adding.
 */
function ShiftSummary({
  waiting,
  washing,
  ready,
}: {
  readonly waiting: readonly Ticket[];
  readonly washing: readonly Ticket[];
  readonly ready: readonly Ticket[];
}) {
  const t = useI18n();
  const readyValue = ready.reduce((n, tk) => n + (tk.snapshot.total ?? 0), 0);
  const longestMs = waiting.reduce((worst, tk) => {
    const at = tk.createdAt?.toMillis
      ? tk.createdAt.toMillis()
      : (tk.createdAt?.seconds ?? 0) * 1000;
    return at ? Math.max(worst, Date.now() - at) : worst;
  }, 0);
  const longestMins = Math.floor(longestMs / 60_000);
  const longestLabel =
    longestMins <= 0
      ? t.t('dashboard.queue.shiftNone')
      : longestMins >= 60
        ? t.t('dashboard.queue.elapsedHr', { hrs: String(Math.floor(longestMins / 60)) })
        : t.t('dashboard.queue.elapsedMin', { mins: String(longestMins) });

  return (
    <div style={shiftStripStyle}>
      <Stat label={t.t('dashboard.queue.shiftWaiting')} value={String(waiting.length)} />
      <Stat
        label={t.t('dashboard.queue.shiftWashing')}
        value={String(washing.length)}
        tone="info"
      />
      <Stat
        label={t.t('dashboard.queue.shiftReady')}
        value={money.format(readyValue)}
        sub={String(ready.length)}
        tone="success"
      />
      <Stat
        label={t.t('dashboard.queue.shiftLongest')}
        value={longestLabel}
        tone={longestMins >= 40 ? 'danger' : longestMins >= 20 ? 'warn' : undefined}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub?: string;
  readonly tone?: 'info' | 'success' | 'warn' | 'danger';
}) {
  const colour =
    tone === 'info'
      ? 'var(--color-info)'
      : tone === 'success'
        ? 'var(--color-primary)'
        : tone === 'warn'
          ? 'var(--color-warn)'
          : tone === 'danger'
            ? 'var(--color-danger)'
            : 'var(--color-on-surface)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span
        style={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--color-on-surface-faint)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--text-h2)',
          fontWeight: 680,
          letterSpacing: '-0.02em',
          color: colour,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
        {sub ? (
          <span
            style={{
              fontSize: 'var(--text-meta)',
              fontWeight: 600,
              color: 'var(--color-on-surface-faint)',
              marginLeft: '0.4em',
            }}
          >
            ×{sub}
          </span>
        ) : null}
      </span>
    </div>
  );
}

const shiftStripStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))',
  gap: 'var(--space-5)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-1)',
  padding: 'var(--space-4) var(--space-5)',
  marginBottom: 'var(--space-4)',
};

function TicketCard({
  ticket,
  onAdvance,
  showReorder,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onDragHandlePointerDown,
  dragHandleLabel,
  bayChip,
  subStageControl,
}: {
  readonly ticket: Ticket;
  readonly onAdvance: (to: TicketStatus) => void;
  readonly showReorder?: boolean;
  readonly canMoveUp?: boolean;
  readonly canMoveDown?: boolean;
  readonly onMoveUp?: () => void;
  readonly onMoveDown?: () => void;
  // When provided (drag-enabled column only), renders a dedicated grip handle
  // that starts the Framer drag. Kept separate from the whole card so the
  // advance/hold/cancel buttons stay clickable (item has `dragListener={false}`).
  readonly onDragHandlePointerDown?: (e: React.PointerEvent) => void;
  readonly dragHandleLabel?: string;
  // In-Washing column only, when the ticket has an assigned bay: a small chip
  // showing the bay's current name.
  readonly bayChip?: React.ReactNode;
  // In-Washing column only, when the outlet has sub-stages enabled: the
  // in-bay/drying/quality-check stepper for this ticket.
  readonly subStageControl?: React.ReactNode;
}) {
  const t = useI18n();
  const total = ticket.snapshot.total;
  const primary = primaryActionFor(ticket.status);
  return (
    <motion.article
      layout
      layoutId={ticket.id}
      // Named so the card is announced as the ticket it is rather than as
      // "article", and so a test can act on ONE car's controls: several cards
      // carry an identically-labelled Start button, and an unscoped query
      // silently picks whichever is topmost.
      aria-label={t.t('dashboard.queue.cardAria', { number: ticket.number })}
      initial={{ opacity: 0, y: 6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.7 }}
      className="k-press"
      style={{
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface)',
        boxShadow: '0 1px 2px var(--color-border), 0 4px 12px -8px var(--color-border-strong)',
        display: 'block',
        transition: 'box-shadow 180ms ease, transform 180ms ease',
      }}
    >
      {showReorder ? (
        <div
          style={{
            display: 'none',
          }}
        >
          {onDragHandlePointerDown ? (
            <button
              type="button"
              aria-label={dragHandleLabel}
              onPointerDown={onDragHandlePointerDown}
              style={dragHandleStyle}
            >
              {GRIP_GLYPH}
            </button>
          ) : null}
          <button
            type="button"
            aria-label={t.t('dashboard.queue.moveUp')}
            disabled={!canMoveUp}
            onClick={() => canMoveUp && onMoveUp?.()}
            style={chevronStyle(!!canMoveUp)}
          >
            {CHEVRON_UP}
          </button>
          <button
            type="button"
            aria-label={t.t('dashboard.queue.moveDown')}
            disabled={!canMoveDown}
            onClick={() => canMoveDown && onMoveDown?.()}
            style={chevronStyle(!!canMoveDown)}
          >
            {CHEVRON_DOWN}
          </button>
        </div>
      ) : null}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 'var(--space-2)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5em', minWidth: 0 }}>
            {showReorder && onDragHandlePointerDown ? (
              <button
                type="button"
                aria-label={dragHandleLabel}
                onPointerDown={onDragHandlePointerDown}
                style={inlineGripStyle}
              >
                {GRIP_GLYPH}
              </button>
            ) : null}
            <strong
              style={{
                fontSize: 'var(--text-body)',
                fontWeight: 650,
                letterSpacing: '-0.01em',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              #{ticket.number}
            </strong>
            <ElapsedChip since={ticket.createdAt} />
          </span>
          <span
            style={{
              fontSize: 'var(--text-body)',
              fontWeight: 600,
              color: 'var(--color-on-surface)',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {money.format(total)}
          </span>
        </div>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: `var(--space-1) 0 0 0`,
          }}
        >
          {ticket.snapshot.packageName ?? '—'} · {ticket.snapshot.plate ?? '—'}
          {typeof ticket.position === 'number' ? ` · #${ticket.position} in line` : ''}
        </p>
        {/*
          Who left this car.

          A washer needs to know whose car this is, and a cashier calling
          "ready for collection" needs a number to match against. The card
          carried neither until 2026-08-21 — the ticket schema had declared
          `customerName` and `customerPhoneMasked` since it was written and
          nothing ever populated or rendered them.

          Omitted entirely when absent rather than rendered as a dash: an
          always-present row of placeholders is a row staff learn to skip. The
          phone is masked server-side in bookTicket, because this screen sits
          on a counter in view of whoever is standing at it.
        */}
        {ticket.customerName || ticket.customerPhoneMasked ? (
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: `var(--space-1) 0 0 0`,
              display: 'flex',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            {ticket.customerName ? <span>{ticket.customerName}</span> : null}
            {ticket.customerPhoneMasked ? (
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {ticket.customerPhoneMasked}
              </span>
            ) : null}
          </p>
        ) : null}
        {bayChip ? <div style={{ marginTop: 'var(--space-2)' }}>{bayChip}</div> : null}
        {primary ? (
          <div
            style={{
              marginTop: 'var(--space-3)',
              display: 'flex',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="primary"
              onClick={() => {
                feel('tap');
                onAdvance(primary.to);
              }}
            >
              {t.t(primary.labelKey)}
            </Button>
            {ticket.status === 'inQueue' ? (
              <Button
                variant="secondary"
                onClick={() => onAdvance('hold')}
                title={t.t('dashboard.queue.holdTooltip')}
              >
                {t.t('dashboard.queue.holdAction')}
              </Button>
            ) : null}
            {ticket.status !== 'readyForCollection' ? (
              <Button variant="danger" onClick={() => onAdvance('cancelled')}>
                {t.t('common.cancel')}
              </Button>
            ) : null}
            {showReorder ? (
              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 2 }}>
                <button
                  type="button"
                  aria-label={t.t('dashboard.queue.moveUp')}
                  disabled={!canMoveUp}
                  onClick={() => canMoveUp && onMoveUp?.()}
                  style={inlineArrowStyle(!!canMoveUp)}
                >
                  {CHEVRON_UP}
                </button>
                <button
                  type="button"
                  aria-label={t.t('dashboard.queue.moveDown')}
                  disabled={!canMoveDown}
                  onClick={() => canMoveDown && onMoveDown?.()}
                  style={inlineArrowStyle(!!canMoveDown)}
                >
                  {CHEVRON_DOWN}
                </button>
              </span>
            ) : null}
          </div>
        ) : null}
        {subStageControl}
      </div>
    </motion.article>
  );
}

/**
 * Wash sub-stage stepper for an In-Washing card. Shows the three internal
 * stages as a segmented control (the active pill IS the current-stage chip)
 * plus a primary "advance to next" action driven by the pure `nextSubStage`
 * cycler. Tapping the active pill clears the sub-stage. Optimistic with
 * revert-on-failure so the card never shows a stage the server refused.
 */
function SubStageControl({
  current,
  onSet,
}: {
  readonly current: TicketSubStage | null;
  readonly onSet: (next: TicketSubStage | null) => Promise<boolean>;
}) {
  const t = useI18n();
  const [optimistic, setOptimistic] = useState<TicketSubStage | null>(current ?? null);
  const [busy, setBusy] = useState(false);

  // Re-seed from server truth whenever it changes and we're not mid-write.
  useEffect(() => {
    if (!busy) setOptimistic(current ?? null);
  }, [current, busy]);

  async function apply(next: TicketSubStage | null): Promise<void> {
    if (busy) return;
    const prev = optimistic;
    setOptimistic(next);
    setBusy(true);
    feel('tap');
    const ok = await onSet(next);
    setBusy(false);
    if (!ok) setOptimistic(prev); // server rejected — snap back to its truth
  }

  const upcoming = nextSubStage(optimistic ?? undefined);

  return (
    <div style={subStageWrapStyle}>
      <span style={subStageLabelStyle}>{t.t('dashboard.queue.subStage.label')}</span>
      <div
        role="group"
        aria-label={t.t('dashboard.queue.subStage.groupLabel')}
        style={subStageGroupStyle}
      >
        {SUB_STAGE_ORDER.map((stage) => {
          const active = optimistic === stage;
          return (
            <button
              key={stage}
              type="button"
              aria-pressed={active}
              disabled={busy}
              onClick={() => void apply(active ? null : stage)}
              style={subStagePillStyle(active, busy)}
            >
              {t.t(`dashboard.queue.subStage.stage.${stage}`)}
            </button>
          );
        })}
      </div>
      {upcoming ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => void apply(upcoming)}
          style={subStageAdvanceStyle(busy)}
        >
          {t.t('dashboard.queue.subStage.advance', {
            stage: t.t(`dashboard.queue.subStage.stage.${upcoming}`),
          })}
        </button>
      ) : (
        <span style={subStageFinalHintStyle}>{t.t('dashboard.queue.subStage.finalHint')}</span>
      )}
    </div>
  );
}

/**
 * Owner/manager-only per-outlet opt-in for wash sub-stages. Writes
 * `catalogSettings.allowSubStages` on the active outlet doc via the existing
 * outlet repo (no direct Firestore SDK). Optimistic with revert-on-failure.
 */
function SubStagesSettingToggle({
  tenantId,
  outletId,
  enabled,
  onError,
}: {
  readonly tenantId: string;
  readonly outletId: string;
  readonly enabled: boolean;
  readonly onError: () => void;
}) {
  const t = useI18n();
  const [optimistic, setOptimistic] = useState(enabled);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!busy) setOptimistic(enabled);
  }, [enabled, busy]);

  async function toggle(): Promise<void> {
    if (busy) return;
    const next = !optimistic;
    setOptimistic(next);
    setBusy(true);
    try {
      await outletDocRepoFor(tenantId).update(outletId, {
        'catalogSettings.allowSubStages': next,
      });
      feel('success');
    } catch (err) {
      logger.error('allowSubStages toggle failed', { outletId, next, error: String(err) });
      setOptimistic(!next); // revert
      onError();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={subStageSettingStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-meta)' }}>
          {t.t('dashboard.queue.subStageSettings.title')}
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t.t('dashboard.queue.subStageSettings.helper')}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={optimistic}
        aria-label={t.t('dashboard.queue.subStageSettings.toggleLabel')}
        disabled={busy}
        onClick={() => void toggle()}
        style={switchTrackStyle(optimistic, busy)}
      >
        <span style={switchKnobStyle(optimistic)} />
      </button>
    </section>
  );
}

/**
 * A small chip showing the bay assigned to an In-Washing car. `name` is
 * already resolved to the current locale by the parent (`resolveBayName`);
 * `null` means the bay id no longer resolves to a known bay — we still show a
 * graceful "bay assigned" chip rather than silently dropping the assignment.
 */
function BayChip({ name }: { readonly name: string | null }) {
  const t = useI18n();
  const display = name ?? t.t('dashboard.queue.bay.chipUnknown');
  return (
    <span style={bayChipStyle} aria-label={t.t('dashboard.queue.bay.chipAria', { name: display })}>
      {t.t('dashboard.queue.bay.chipLabel', { name: display })}
    </span>
  );
}

/**
 * Owner/manager-only summary card for wash bays, sitting beside the sub-stage
 * toggle in the queue header. Opens the manager modal — the actual CRUD lives
 * there to keep the header compact.
 */
function BaysSettingPanel({
  totalCount,
  activeCount,
  onManage,
}: {
  readonly totalCount: number;
  readonly activeCount: number;
  readonly onManage: () => void;
}) {
  const t = useI18n();
  return (
    <section style={subStageSettingStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-meta)' }}>
          {t.t('dashboard.queue.bay.settingsTitle')}
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {totalCount === 0
            ? t.t('dashboard.queue.bay.settingsHelperEmpty')
            : t.t('dashboard.queue.bay.settingsHelper', {
                active: activeCount,
                total: totalCount,
              })}
        </p>
      </div>
      <button type="button" onClick={onManage} style={ghostButtonStyle}>
        {t.t('dashboard.queue.bay.manage')}
      </button>
    </section>
  );
}

/**
 * Start-Next bay chooser. Shown only when the outlet has ≥1 active bay. Picking
 * a bay passes its id into the advance call; "Start without a bay" starts the
 * wash with no bayId (no forced step); Cancel backs out (no dead-end).
 */
function BayPicker({
  ticket,
  bays,
  onPick,
  onCancel,
}: {
  readonly ticket: Ticket;
  readonly bays: readonly Bay[];
  readonly onPick: (bayId: string | null) => void;
  readonly onCancel: () => void;
}) {
  const t = useI18n();
  return (
    <div style={modalOverlayStyle} role="presentation" onClick={onCancel}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t.t('dashboard.queue.bay.pickTitle', { number: ticket.number })}
        style={modalCardStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={modalTitleStyle}>
          {t.t('dashboard.queue.bay.pickTitle', { number: ticket.number })}
        </h2>
        <p style={modalHelperStyle}>{t.t('dashboard.queue.bay.pickHelper')}</p>
        <div style={bayPickGridStyle}>
          {bays.map((bay) => (
            <button
              key={bay.id}
              type="button"
              onClick={() => onPick(bay.id)}
              style={bayPickButtonStyle}
            >
              {bayDisplayName(bay, t.locale)}
            </button>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            marginTop: 'var(--space-3)',
          }}
        >
          <button type="button" onClick={() => onPick(null)} style={ghostButtonStyle}>
            {t.t('dashboard.queue.bay.pickNone')}
          </button>
          <button type="button" onClick={onCancel} style={ghostButtonStyle}>
            {t.t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Owner/manager bay-config modal: list, add, rename, and activate/deactivate
 * bays. Deactivation is a SOFT delete (`active:false`) so historical
 * `ticket.bayId` refs stay resolvable. Bay names are stored identically across
 * every locale (bay names like "Bay 1"/"Detailing" aren't translated). Writes
 * go through the client repo; the live snapshot at the page level reflects the
 * result, so no local optimistic copy is needed. Failures show an inline error.
 */
function BaysManagerModal({
  tenantId,
  outletId,
  bays,
  onClose,
}: {
  readonly tenantId: string;
  readonly outletId: string;
  readonly bays: readonly Bay[];
  readonly onClose: () => void;
}) {
  const t = useI18n();
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Store the same string across every locale — bay names aren't translated.
  const asI18nName = (v: string) => ({ en: v, ms: v, zh: v, ta: v });

  async function addBay(): Promise<void> {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    const id = `bay-${crypto.randomUUID().slice(0, 12)}`;
    try {
      await baysRepoFor(tenantId, outletId).set(id, { id, name: asI18nName(name), active: true });
      feel('success');
      setNewName('');
    } catch (err) {
      logger.error('addBay failed', { outletId, error: String(err) });
      setError(t.t('dashboard.queue.bay.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(bay: Bay): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await baysRepoFor(tenantId, outletId).update(bay.id, { active: !bay.active });
      feel('tap');
    } catch (err) {
      logger.error('toggleBay failed', { bayId: bay.id, error: String(err) });
      setError(t.t('dashboard.queue.bay.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  function startRename(bay: Bay): void {
    setRenamingId(bay.id);
    setRenameValue(bayDisplayName(bay, t.locale));
    setError(null);
  }

  async function saveRename(bay: Bay): Promise<void> {
    const name = renameValue.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      await baysRepoFor(tenantId, outletId).update(bay.id, { name: asI18nName(name) });
      feel('success');
      setRenamingId(null);
    } catch (err) {
      logger.error('renameBay failed', { bayId: bay.id, error: String(err) });
      setError(t.t('dashboard.queue.bay.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  // Active bays first, so the operator's live picks sit at the top.
  const sorted = [...bays].sort((a, b) => Number(b.active) - Number(a.active));

  return (
    <div style={modalOverlayStyle} role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t.t('dashboard.queue.bay.managerTitle')}
        style={modalCardStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
          }}
        >
          <h2 style={modalTitleStyle}>{t.t('dashboard.queue.bay.managerTitle')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t.t('dashboard.queue.bay.close')}
            style={dismissButtonStyle}
          >
            {DISMISS_GLYPH}
          </button>
        </div>
        <p style={modalHelperStyle}>{t.t('dashboard.queue.bay.managerHelper')}</p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void addBay();
          }}
          style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}
        >
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t.t('dashboard.queue.bay.namePlaceholder')}
            aria-label={t.t('dashboard.queue.bay.nameLabel')}
            autoComplete="off"
            maxLength={40}
            style={bayInputStyle}
          />
          <button type="submit" disabled={busy || !newName.trim()} style={primaryButtonStyle}>
            {t.t('dashboard.queue.bay.add')}
          </button>
        </form>

        {error ? (
          <p role="alert" style={bayErrorTextStyle}>
            {error}
          </p>
        ) : null}

        {sorted.length === 0 ? (
          <p style={{ ...modalHelperStyle, marginTop: 'var(--space-4)' }}>
            {t.t('dashboard.queue.bay.empty')}
          </p>
        ) : (
          <ul style={bayListStyle}>
            {sorted.map((bay) => (
              <li key={bay.id} style={bayRowStyle}>
                {renamingId === bay.id ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void saveRename(bay);
                    }}
                    style={{ display: 'flex', gap: 'var(--space-2)', flex: 1, minWidth: 0 }}
                  >
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      aria-label={t.t('dashboard.queue.bay.renameLabel')}
                      autoComplete="off"
                      maxLength={40}
                      style={bayInputStyle}
                    />
                    <button
                      type="submit"
                      disabled={busy || !renameValue.trim()}
                      style={primaryButtonStyle}
                    >
                      {t.t('dashboard.queue.bay.save')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRenamingId(null)}
                      style={ghostButtonStyle}
                    >
                      {t.t('common.cancel')}
                    </button>
                  </form>
                ) : (
                  <>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {bayDisplayName(bay, t.locale)}
                      </span>
                      <span style={bay.active ? bayActiveBadgeStyle : bayInactiveBadgeStyle}>
                        {bay.active
                          ? t.t('dashboard.queue.bay.activeBadge')
                          : t.t('dashboard.queue.bay.inactiveBadge')}
                      </span>
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => startRename(bay)}
                        disabled={busy}
                        style={ghostButtonStyle}
                      >
                        {t.t('dashboard.queue.bay.rename')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void toggleActive(bay)}
                        disabled={busy}
                        style={ghostButtonStyle}
                      >
                        {bay.active
                          ? t.t('dashboard.queue.bay.deactivate')
                          : t.t('dashboard.queue.bay.activate')}
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function HoldSection({
  tickets,
  onResume,
  onCancel,
}: {
  readonly tickets: readonly Ticket[];
  readonly onResume: (ticket: Ticket) => void;
  readonly onCancel: (ticket: Ticket) => void;
}) {
  const t = useI18n();
  return (
    <details
      style={{
        marginTop: 'var(--space-4)',
        paddingTop: 'var(--space-3)',
        borderTop: '1px dashed var(--color-border-strong)',
      }}
    >
      <summary
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          cursor: 'pointer',
          listStyle: 'none',
          padding: 'var(--space-1) 0',
        }}
      >
        {t.t('dashboard.queue.onHoldCount', { count: tickets.length })}
      </summary>
      <div style={{ display: 'grid', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
        {tickets.map((tk) => (
          <article
            key={tk.id}
            style={{
              padding: 'var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: 'var(--tint-subtle)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
              }}
            >
              <strong style={{ fontSize: 'var(--text-body)' }}>#{tk.number}</strong>
              <span
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {tk.snapshot.plate ?? '—'}
              </span>
            </div>
            <p
              style={{
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                margin: 'var(--space-1) 0 0 0',
              }}
            >
              {tk.snapshot.packageName ?? '—'}
            </p>
            <div style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 'var(--space-2)' }}>
              <button type="button" onClick={() => onResume(tk)} style={primaryButtonStyle}>
                {t.t('dashboard.queue.resume')}
              </button>
              <button type="button" onClick={() => onCancel(tk)} style={ghostButtonStyle}>
                {t.t('common.cancel')}
              </button>
            </div>
          </article>
        ))}
      </div>
    </details>
  );
}

// ── Appointments (scheduled tickets) ───────────────────────────────────────

/** Full weekday + date + time for an appointment instant (operator-local tz). */
function formatApptWhen(ms: number, locale: string): string {
  if (!ms) return '—';
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(ms));
}

/** Time-of-day only, for a due appointment (it's already today). */
function formatApptTime(ms: number, locale: string): string {
  if (!ms) return '—';
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(
    new Date(ms),
  );
}

/** The "Upcoming appointments" section — not-yet-due `scheduled` tickets. */
function UpcomingAppointmentsSection({
  tickets,
  locale,
  canManage,
  onCancel,
}: {
  readonly tickets: readonly Ticket[];
  readonly locale: string;
  readonly canManage: boolean;
  readonly onCancel: (ticket: Ticket) => void;
}) {
  const t = useI18n();
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        boxShadow: 'var(--shadow-1)',
        marginBottom: 'var(--space-4)',
        borderLeft: '3px solid var(--color-primary)',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 'var(--space-3)',
          gap: 'var(--space-2)',
        }}
      >
        <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
          {t.t('dashboard.queue.appointments.upcomingTitle')}
        </h2>
        <span
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {tickets.length}
        </span>
      </header>
      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        {tickets.map((tk) => (
          <article
            key={tk.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: 'var(--tint-subtle)',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <strong style={{ fontSize: 'var(--text-body)', fontVariantNumeric: 'tabular-nums' }}>
                {formatApptWhen(scheduledForMsOf(tk), locale)}
              </strong>
              <p
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  margin: 'var(--space-1) 0 0 0',
                }}
              >
                #{tk.number} · {tk.snapshot.plate ?? '—'} · {tk.snapshot.packageName ?? '—'}
              </p>
            </div>
            {canManage ? (
              <button type="button" onClick={() => onCancel(tk)} style={ghostButtonStyle}>
                {t.t('common.cancel')}
              </button>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

/** Due appointments surfaced at the top of the In-Queue column (Admit/Cancel). */
function DueAppointmentsStrip({
  tickets,
  locale,
  canManage,
  onAdmit,
  onCancel,
}: {
  readonly tickets: readonly Ticket[];
  readonly locale: string;
  readonly canManage: boolean;
  readonly onAdmit: (ticket: Ticket) => void;
  readonly onCancel: (ticket: Ticket) => void;
}) {
  const t = useI18n();
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
      {tickets.map((tk) => (
        <article
          key={tk.id}
          style={{
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-primary)',
            background: 'rgba(var(--color-primary-rgb), 0.06)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 'var(--space-2)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-caption, var(--text-meta))',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 1,
                color: 'var(--color-primary)',
              }}
            >
              {t.t('dashboard.queue.appointments.dueBadge')}
            </span>
            <span
              style={{
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatApptTime(scheduledForMsOf(tk), locale)}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginTop: 'var(--space-1)',
            }}
          >
            <strong style={{ fontSize: 'var(--text-body)' }}>#{tk.number}</strong>
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {tk.snapshot.plate ?? '—'}
            </span>
          </div>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 'var(--space-1) 0 0 0',
            }}
          >
            {tk.snapshot.packageName ?? '—'}
          </p>
          {canManage ? (
            <div style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 'var(--space-2)' }}>
              <button type="button" onClick={() => onAdmit(tk)} style={primaryButtonStyle}>
                {t.t('dashboard.queue.appointments.admit')}
              </button>
              <button type="button" onClick={() => onCancel(tk)} style={ghostButtonStyle}>
                {t.t('common.cancel')}
              </button>
            </div>
          ) : null}
        </article>
      ))}
    </div>
  );
}

/** Decorative reorder-arrow, grip and dismiss glyphs. Kept as named constants
 *  (not JSX text) so they carry no translatable copy — the buttons' accessible
 *  names come from their `aria-label`s. */
const CHEVRON_UP = '▲';
const CHEVRON_DOWN = '▼';
const GRIP_GLYPH = '⠿';
const DISMISS_GLYPH = '✕';

function chevronStyle(enabled: boolean): React.CSSProperties {
  return {
    width: 28,
    height: 24,
    padding: 0,
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    background: 'transparent',
    color: enabled ? 'var(--color-on-surface)' : 'var(--color-border-strong)',
    cursor: enabled ? 'pointer' : 'not-allowed',
    fontSize: 10,
    lineHeight: 1,
  };
}

const dragHandleStyle: React.CSSProperties = {
  width: 'var(--touch-target)',
  minHeight: 'var(--touch-target)',
  padding: 0,
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  cursor: 'grab',
  fontSize: 16,
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Let the handle own touch gestures so a touch-drag doesn't scroll the column.
  touchAction: 'none',
};

const reorderErrorBannerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  marginBottom: 'var(--space-4)',
  padding: 'var(--space-3) var(--space-4)',
  borderRadius: 'var(--radius-md)',
  background: 'rgba(220,38,38,0.08)',
  border: '1px solid rgba(220,38,38,0.24)',
  color: 'var(--color-on-surface)',
  fontSize: 'var(--text-meta)',
};

const dismissButtonStyle: React.CSSProperties = {
  width: 'var(--touch-target)',
  minHeight: 'var(--touch-target)',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  cursor: 'pointer',
  fontSize: 16,
  lineHeight: 1,
  flexShrink: 0,
};

/**
 * The next move for a ticket, as a translation KEY rather than a label.
 *
 * These used to be literal 'Start' / 'Ready' / 'Collected'. They are rendered
 * as button text on the queue board, so every non-English operator read three
 * English words on the screen they spend the shift in. The i18n lint missed it
 * because a string in a .ts object is not a JSX text node — the rule only sees
 * the latter.
 *
 * The wording matches the `queueStatus.*` chip beside it in each locale, so the
 * button and the status it moves to are named with the same word.
 */
function primaryActionFor(status: TicketStatus): { to: TicketStatus; labelKey: string } | null {
  switch (status) {
    case 'inQueue':
      return { to: 'inWashingProcess', labelKey: 'dashboard.queue.action.start' };
    case 'inWashingProcess':
      return { to: 'readyForCollection', labelKey: 'dashboard.queue.action.ready' };
    case 'readyForCollection':
      return { to: 'collected', labelKey: 'dashboard.queue.action.collected' };
    default:
      return null;
  }
}

const ctaLinkStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
};

// ── wash sub-stage control + settings toggle styles ──────────────────────

const subStageWrapStyle: React.CSSProperties = {
  marginTop: 'var(--space-3)',
  paddingTop: 'var(--space-3)',
  borderTop: '1px dashed var(--color-border-strong)',
  display: 'grid',
  gap: 'var(--space-2)',
};

const subStageLabelStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const subStageGroupStyle: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  padding: 4,
  borderRadius: 'var(--radius-md)',
  background: 'var(--tint-subtle)',
};

function subStagePillStyle(active: boolean, busy: boolean): React.CSSProperties {
  return {
    flex: 1,
    minWidth: 0,
    padding: '8px 10px',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    background: active ? 'var(--color-primary)' : 'transparent',
    color: active ? 'var(--color-primary-fg)' : 'var(--color-on-surface-muted)',
    fontSize: 'var(--text-meta)',
    fontWeight: active ? 600 : 500,
    cursor: busy ? 'wait' : 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: 'background 120ms ease, color 120ms ease',
  };
}

function subStageAdvanceStyle(busy: boolean): React.CSSProperties {
  return {
    justifySelf: 'start',
    background: 'transparent',
    color: 'var(--color-primary)',
    padding: '6px 12px',
    border: '1px solid var(--color-primary)',
    borderRadius: 'var(--radius-pill)',
    fontSize: 'var(--text-meta)',
    fontWeight: 500,
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };
}

const subStageFinalHintStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const subStageSettingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-4)',
  marginBottom: 'var(--space-4)',
  padding: 'var(--space-3) var(--space-4)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  boxShadow: 'var(--shadow-1)',
};

function switchTrackStyle(on: boolean, busy: boolean): React.CSSProperties {
  return {
    position: 'relative',
    flexShrink: 0,
    width: 46,
    height: 'var(--touch-target)',
    minHeight: 'var(--touch-target)',
    padding: 3,
    border: 'none',
    borderRadius: 'var(--radius-pill)',
    background: on ? 'var(--color-primary)' : 'var(--color-border-strong)',
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.7 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'background 140ms ease',
  };
}

function switchKnobStyle(on: boolean): React.CSSProperties {
  return {
    display: 'block',
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: 'var(--color-primary-fg, #fff)',
    boxShadow: 'var(--shadow-1)',
    transform: on ? 'translateX(22px)' : 'translateX(0)',
    transition: 'transform 140ms ease',
  };
}

// ── wash-bay picker / config styles ──────────────────────────────────────

const bayChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  maxWidth: '100%',
  padding: '2px 10px',
  borderRadius: 'var(--radius-pill)',
  background: 'rgba(13,122,79,0.10)',
  color: 'var(--color-primary)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,0.4)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 200,
  padding: 'var(--space-4)',
};

const modalCardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-6)',
  maxWidth: 520,
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: 'var(--shadow-3)',
  display: 'grid',
  gap: 'var(--space-2)',
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-h2)',
  fontWeight: 600,
};

const modalHelperStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};

const bayPickGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 120px), 1fr))',
  gap: 'var(--space-2)',
  marginTop: 'var(--space-3)',
};

const bayPickButtonStyle: React.CSSProperties = {
  padding: '12px 14px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  fontSize: 'var(--text-body)',
  fontWeight: 500,
  cursor: 'pointer',
  minHeight: 'var(--touch-target)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const bayInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: '8px 12px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  fontSize: 'var(--text-meta)',
  minHeight: 'var(--touch-target)',
};

const bayErrorTextStyle: React.CSSProperties = {
  margin: 'var(--space-2) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: '#dc2626',
};

const bayListStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: 'var(--space-3) 0 0 0',
  padding: 0,
  display: 'grid',
  gap: 'var(--space-2)',
};

const bayRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
  background: 'var(--tint-subtle)',
};

const bayActiveBadgeStyle: React.CSSProperties = {
  flexShrink: 0,
  padding: '1px 8px',
  borderRadius: 'var(--radius-pill)',
  background: 'rgba(13,122,79,0.12)',
  color: 'var(--color-primary)',
  fontSize: 'var(--text-caption, 11px)',
  fontWeight: 600,
};

const bayInactiveBadgeStyle: React.CSSProperties = {
  flexShrink: 0,
  padding: '1px 8px',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-border)',
  color: 'var(--color-on-surface-muted)',
  fontSize: 'var(--text-caption, 11px)',
  fontWeight: 600,
};

/**
 * Layout-matched loading state for the Kanban board — mirrors the real
 * header + three-column grid so the shell doesn't jump when tickets arrive.
 * Shimmer blocks come from the shared `@kilat/ui` Skeleton (reduced-motion
 * safe); columns stagger in via `k-rise`. Carries no visible text — the
 * `label` is screen-reader-only status.
 */
function QueueBoardSkeleton({ label }: { readonly label: string }) {
  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <span role="status" aria-live="polite" style={queueSrOnly}>
        {label}
      </span>
      <header
        aria-hidden
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <Skeleton width={180} height={28} />
          <Skeleton width={120} height={14} />
        </div>
        <Skeleton width={132} height={40} radius="var(--radius-md)" />
      </header>
      <div
        aria-hidden
        style={{
          display: 'grid',
          // Same columns as the real board (above), so a phone sees one stacked
          // column instead of three squeezed ones with cards spilling out.
          gridTemplateColumns: BOARD_COLUMNS,
          gap: 'var(--space-4)',
          alignItems: 'start',
        }}
      >
        {[0, 1, 2].map((colIndex) => (
          <section
            key={colIndex}
            className="k-rise"
            style={
              {
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                boxShadow: 'var(--shadow-1)',
                minHeight: 360,
                '--k-i': colIndex,
              } as CSSProperties
            }
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-3)',
              }}
            >
              <Skeleton width={110} height={16} />
              <Skeleton width={20} height={16} />
            </div>
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              {[0, 1, 2].map((cardIndex) => (
                <div
                  key={cardIndex}
                  style={{
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    display: 'grid',
                    gap: 'var(--space-2)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Skeleton width={44} height={16} />
                    <Skeleton width={52} height={16} />
                  </div>
                  <Skeleton width="80%" height={12} />
                  <Skeleton width={92} height={30} radius="var(--radius-sm)" />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}

function Splash({ label, action }: { readonly label: string; readonly action?: React.ReactNode }) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div style={{ display: 'grid', gap: 'var(--space-4)', justifyItems: 'center' }}>
        <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {label}
        </p>
        {action}
      </div>
    </main>
  );
}

/** Small inline drag grip — replaces the tall rail that padded every card out. */
const inlineGripStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  cursor: 'grab',
  touchAction: 'none',
  lineHeight: 1,
  flexShrink: 0,
};

/** Reorder arrows, inline at the end of the action row rather than in a rail. */
function inlineArrowStyle(enabled: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-surface)',
    color: enabled ? 'var(--color-on-surface)' : 'rgba(15,23,42,0.22)',
    cursor: enabled ? 'pointer' : 'not-allowed',
    padding: 0,
    fontSize: '0.7rem',
    lineHeight: 1,
  };
}

/** The board's columns — shared by the live board and its skeleton so the two
 *  can never disagree (the skeleton once forced three squeezed columns onto a
 *  phone while the board itself stacked them). */
const BOARD_COLUMNS = 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))';
